/**
 * 🔐 Hook para Autenticação Persistente
 * Gerencia login automático e persistência de sessão
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAccessToken, isAuthenticated, validateToken, checkAndRefreshToken, refreshAccessToken } from '../Backend/services/authService';
import { setUserProfile, UserInfo } from '../redux/slices/userProfileSlice';
import { UserData } from '../Backend/types';
import { RootState } from '../redux/store';
import { limparTokens } from '@/security/tokens';
import { SessionRefreshError } from '@/security/refreshSession';

/**
 * 🔄 Converte UserData para UserInfo com valores padrão
 */
const convertUserDataToUserInfo = (userData: UserData): UserInfo => {
  return {
    id: userData.id,
    email: userData.email,
    name: userData.name,
    username: userData.username || '',
    biography: userData.biography || '',
    club: userData.club || '',
    date_of_birth: userData.date_of_birth || null,
    phone_number: userData.phone_number || '',
    location: userData.location || '',
    local_play: userData.local_play || '',
    profile_picture: userData.profile_picture || '',
    gender: userData.gender || '',
    subscription_type: userData.subscription_type || '',
    // 🔧 Converter datas para strings serializáveis no Redux
    date_joined: userData.date_joined ? new Date(userData.date_joined).toISOString() : new Date().toISOString(),
    subscription_expiration: userData.subscription_expiration ? new Date(userData.subscription_expiration).toISOString() : new Date().toISOString()
  };
};

export const useAuthPersistence = () => {
  const dispatch = useDispatch();
  const userProfile = useSelector((state: RootState) => state.userProfile.userProfile);
  
  useEffect(() => {
    const restoreSession = async () => {
      console.log('🔍 [AUTH] Verificando sessão salva...');
      
      // Verificar se há token salvo
      if (!isAuthenticated()) {
        console.log('ℹ️ [AUTH] Nenhuma sessão encontrada');
        return;
      }

      const token = getAccessToken();
      if (!token) {
        console.log('⚠️ [AUTH] Token não encontrado');
        return;
      }

      try {
        console.log('🔄 [AUTH] Validando token e carregando dados do usuário...');

        // Se o access token expirou (dura 1 dia), renovar primeiro com o
        // refresh token (dura 90 dias) — só perdemos a sessão se nem o
        // refresh funcionar. Era isto que faltava: antes, o token expirado
        // apagava a sessão e pedia login todos os dias.
        await checkAndRefreshToken();
        let userData = await validateToken(getAccessToken() || token);

        // Última tentativa antes de desistir: renovar e revalidar
        if (!userData) {
          const renewed = await refreshAccessToken();
          if (renewed) {
            userData = await validateToken(renewed);
          }
        }

        if (userData) {
          console.log('✅ [AUTH] Token válido! Sessão ativa mantida.');
          console.log('👤 [AUTH] Dados do usuário carregados:', {
            id: userData.id,
            email: userData.email,
            name: userData.name
          });
          
          // Carregar dados do usuário no Redux (convertendo tipos)
          const userInfo = convertUserDataToUserInfo(userData);
          dispatch(setUserProfile(userInfo));
          localStorage.setItem('sessionRestored', 'true');
          
          console.log('🎯 [AUTH] Sessão restaurada com sucesso!');
          
        } else {
          console.log('❌ [AUTH] Token inválido ou expirado');
          // Limpar tokens inválidos
          clearSession();
        }
      } catch (error) {
        console.error('❌ [AUTH] Erro ao validar token:', error);
        
        // Se for erro de rede, manter sessão local temporariamente
        if (!(error instanceof SessionRefreshError && error.expired)) {
          console.log('⚠️ [AUTH] Erro de rede - mantendo sessão local temporariamente');
        } else {
          // Para outros erros, limpar sessão
          console.log('🧹 [AUTH] Limpando sessão devido a erro');
          clearSession();
        }
      }
    };

    // Executar apenas se não tem dados no Redux, mas tem token
    if (isAuthenticated() && !userProfile) {
      console.log('🔄 [AUTH] Redux vazio - iniciando restauração de sessão...');
      restoreSession();
    } else if (userProfile) {
      console.log('✅ [AUTH] Dados já carregados no Redux:', userProfile.email);
    } else {
      // Log silencioso - estado conhecido
    }

    // PC do clube fica aberto dias seguidos sem recarregar a página:
    // renovar o token periodicamente enquanto a app estiver aberta
    const refreshInterval = setInterval(() => {
      if (isAuthenticated()) {
        void checkAndRefreshToken().catch((error) => {
          if (error instanceof SessionRefreshError && error.expired) clearSession();
        });
      }
    }, 6 * 60 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [dispatch, userProfile]);

  return {
    isAuthenticated: isAuthenticated(),
    userProfile: userProfile
  };
};

/**
 * 🔄 Função para limpar sessão completamente
 */
export const clearSession = () => {
  console.log('🧹 Limpando sessão...');
  // Todas as convenções de nome, não só duas (ver security/tokens.ts).
  limparTokens();
  localStorage.removeItem('sessionRestored');
  console.log('✅ Sessão limpa');
};

/**
 * 📱 Função para verificar se a sessão está ativa
 */
export const isSessionActive = (): boolean => {
  return isAuthenticated() && !!localStorage.getItem('sessionRestored');
};
